import React from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { Database } from "@/integrations/supabase/types";

type SubscriptionTier = Database['public']['Enums']['subscription_tier'];

interface AccountActionsProps {
  userId: string;
  userEmail: string;
  subscriptionTier: SubscriptionTier;
  onAccountDeleted: () => void;
}

export const AccountActions = ({ 
  userId, 
  userEmail, 
  subscriptionTier,
  onAccountDeleted 
}: AccountActionsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handlePasswordReset = async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(
      userEmail,
      { redirectTo: `${window.location.origin}/account` }
    );

    if (error) {
      toast({
        title: "Error",
        description: "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Password reset email has been sent. Please check your inbox.",
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          subscription_tier: 'free',
          subscription_updated_at: new Date().toISOString(),
          resources_used_this_month: 0
        })
        .eq('id', userId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled and downgraded to the Free tier.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAccount = async () => {
    const { error } = await supabase.auth.admin.deleteUser(userId);
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Account Deleted",
        description: "Your account has been successfully deleted.",
      });
      onAccountDeleted();
    }
  };

  return (
    <div className="space-y-4 pt-4">
      <Button
        variant="outline"
        onClick={handlePasswordReset}
        className="w-full"
      >
        Reset Password
      </Button>

      {subscriptionTier !== 'free' && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full text-yellow-600 hover:text-yellow-700"
            >
              Cancel Subscription
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
              <AlertDialogDescription>
                This will downgrade your account to the Free tier. You'll lose access to premium features and your resource limits will be reduced.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelSubscription}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive"
          >
            Delete Account
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your account and remove your data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};